import {
  CampaignPrivateView,
  CampaignCreate,
  CampaignPublicView,
  CampaignPatch,
  ChapterPatch,
  QuestPatch,
} from "@bbb/model";
import { _apiRequest } from "./_apiClients";

// Ensure we never send server-owned fields in patch payloads; the API should reject them.
const stripQuestPatch = (quest: QuestPatch): QuestPatch => {
  const { createdAt: _qCreated, updatedAt: _qUpdated, ...rest } = quest as any;
  return rest as QuestPatch;
};

const stripChapterPatch = (chapter: ChapterPatch): ChapterPatch => {
  const {
    createdAt: _cCreated,
    updatedAt: _cUpdated,
    quests,
    ...rest
  } = chapter as any;
  return {
    ...(rest as ChapterPatch),
    ...(quests ? { quests: quests.map(stripQuestPatch) } : {}),
  };
};

const stripServerFieldsFromCampaignPatch = (
  patch: CampaignPatch,
): CampaignPatch => {
  const { metadata, ...rest } = patch as any;
  const cleanedMetadata = metadata
    ? {
        ...metadata,
        // RewardConfigPatchSchema is user-owned; remove server-owned fields
        rewardConfig: metadata.rewardConfig
          ? metadata.rewardConfig
          : undefined,
        chapters: metadata.chapters
          ? metadata.chapters.map(stripChapterPatch)
          : undefined,
      }
    : undefined;

  const cleaned = {
    ...(rest as CampaignPatch),
    ...(cleanedMetadata ? { metadata: cleanedMetadata } : {}),
  };

  // Drop undefined keys (esp. rewardConfig when stripped)
  return JSON.parse(JSON.stringify(cleaned));
};

const stripServerFieldsFromCampaignCreate = (
  payload: CampaignCreate,
): CampaignCreate => {
  const {
    id: _id,
    ownerId: _ownerId,
    status: _status,
    statusUpdatedAt: _statusUpdatedAt,
    publishedAt: _publishedAt,
    endedAt: _endedAt,
    totalParticipants: _totalParticipants,
    updatedAt: _updatedAt,
    createdAt: _createdAt,
    ...rest
  } = payload as any;

  return JSON.parse(JSON.stringify(rest));
};

/**
 * Create a new campaign
 * @param token
 * @param payload
 * @returns
 */
export async function saveCampaignApi(token: string, payload: CampaignCreate) {
  const cleaned = stripServerFieldsFromCampaignCreate(payload);

  return _apiRequest<CampaignPrivateView>("/v1/campaigns", {
    method: "POST",
    token,
    data: cleaned,
  });
}

/**
 * Fetch all campaigns of my community
 * @param token
 * @param communityId
 * @returns
 */
export async function fetchPrivateCampaignsApi(
  token: string,
  communityId: string,
) {
  return _apiRequest<{ items: CampaignPrivateView[]; nextCursor?: string }>(
    `/v1/campaigns/mine`,
    {
      token,
      params: { communityId },
    },
  );
}

/**
 * Fetch live campaigns for public/guest view.
 * Supports optional pagination and community filtering.
 */
export async function fetchLiveCampaignsApi(params?: {
  communityId?: string;
  limit?: number;
  cursor?: string;
}) {
  return _apiRequest<{ items: CampaignPublicView[]; nextCursor?: string }>(
    `/v1/campaigns`,
    {
      authRequired: false,
      params,
    },
  );
}

/**
 * Fetch a single 1 campaign
 * returns null if not an owner.
 * @param campaignId
 * @param token
 * @returns
 */
export async function fetchSinglePrivateCampaignApi(
  campaignId: string,
  token?: string,
) {
  return _apiRequest<CampaignPrivateView>(`/v1/campaigns/${campaignId}`, {
    token,
    authRequired: true,
  });
}

export type CampaignNftDeploymentTx = {
  alreadyDeployed: false;
  chainId: number;
  network: string;
  contractName: "CampaignCompletionNFT";
  abi: unknown[];
  bytecode: `0x${string}`;
  args: readonly unknown[];
} | {
  alreadyDeployed: true;
  contractAddress: string;
  deployedAt?: number;
};

export function initiateCampaignNftDeploymentApi(
  campaignId: string,
  adminWalletAddress: string,
) {
  return _apiRequest<CampaignNftDeploymentTx>(
    `/v1/campaigns/${campaignId}/nft/deployment/initiate`,
    {
      method: "POST",
      data: { adminWalletAddress },
    },
  );
}

export function registerCampaignNftDeploymentApi(
  campaignId: string,
  txHash: string,
) {
  return _apiRequest<{
    alreadyDeployed: boolean;
    contractAddress: string;
    deployedAt?: number;
  }>(`/v1/campaigns/${campaignId}/nft/deployment/register`, {
    method: "POST",
    data: { txHash },
  });
}

export type CampaignNftClaim = {
  id: string;
  campaignId: string;
  communityId: string;
  userId: string;
  walletAddress?: string;
  claimHash: `0x${string}`;
  status: "ineligible" | "eligible" | "signature_issued" | "minted";
  signature?: `0x${string}`;
  signatureIssuedAt?: number;
  contractAddress?: string;
  chainId?: number;
  tokenId?: string;
  txHash?: string;
  mintedAt?: number;
  createdAt: number;
  updatedAt: number;
};

export function issueCampaignNftClaimApi(
  token: string,
  campaignId: string,
  walletAddress: string,
) {
  return _apiRequest<CampaignNftClaim>(
    `/v1/campaigns/${campaignId}/nft/claim`,
    {
      method: "POST",
      token,
      data: { walletAddress },
    },
  );
}

export function confirmCampaignNftMintApi(
  token: string,
  campaignId: string,
  txHash: string,
) {
  return _apiRequest<CampaignNftClaim>(
    `/v1/campaigns/${campaignId}/nft/claim/confirm`,
    {
      method: "POST",
      token,
      data: { txHash },
    },
  );
}

type CampaignInsightsDto = {
  campaignId: string;
  communityId: string;
  status: "ready" | "live" | "finalized";
  generatedAt: number;
  version: number;
  totals: {
    submissions: number;
    approved: number;
    rejected: number;
    pending: number;
    uniqueParticipants: number;
    approvalRate: number;
  };
  byQuest: Array<{
    chapterId: string;
    chapterTitle: string | null;
    questId: string;
    questTitle: string | null;
    questType?: string | null;
    submissions: number;
    approved: number;
    rejected?: number;
    pending?: number;
    approvalRate: number;
    uniqueParticipants: number;
    avgScore: number;
    medianTimeToCompleteMs: number;
  }>;
  mostCompletedFirst?: Array<{
    questId: string;
    questTitle: string | null;
    users: number;
    percentage: number;
  }>;
  completionPerQuest?: Array<{
    questId: string;
    questTitle: string | null;
    approvedCount: number;
  }>;
  scoreDistribution: {
    samples: number;
    average: number;
    min: number;
    max: number;
    buckets: Record<"0-49" | "50-69" | "70-84" | "85-100", number>;
  };
  aiAnalysis?: {
    status:
      | "not_generated"
      | "queued"
      | "generating"
      | "skipped"
      | "generated"
      | "failed_retryable"
      | "failed_exhausted";
    startedAt?: number;
    leaseExpiresAt?: number;
    nextAttemptAfter?: number;
    generatedAt?: number;
    skippedAt?: number;
    failedAt?: number;
    attempts?: number;
    reason?: string;
    model?: string;
    promptVersion?: string;
    inputHash?: string;
    summary?: string;
    insightCards?: Array<{
      category:
        | "sequence_insight"
        | "quality_insight"
        | "review_workload_insight"
        | "participation_confidence"
        | "next_campaign_action";
      title: string;
      insight: string;
      recommendation: string;
      metricBasis: string;
      confidence: "low" | "medium" | "high";
      questIds?: string[];
      questTypes?: string[];
    }>;
    strengths?: string[];
    risks?: string[];
    recommendations?: string[];
  };
  updatedAt: number;
};

export async function fetchCampaignInsightsApi(
  token: string | undefined,
  campaignId: string,
) {
  return _apiRequest<CampaignInsightsDto>(`/v1/campaigns/${campaignId}/insights`, {
    token,
  });
}

export async function updateCampaignApi(
  token: string | undefined,
  payload: {
    updates: CampaignPatch;
  },
) {
  const { updates } = payload;
  const cleaned = stripServerFieldsFromCampaignPatch(updates);
  return _apiRequest<CampaignPrivateView>(`/v1/campaigns/${updates.id}`, {
    method: "PATCH",
    token,
    data: cleaned,
  });
}

export async function deleteCampaignApi(
  token: string | undefined,
  campaignId: string,
) {
  return _apiRequest<void>(`/v1/campaigns/${campaignId}`, {
    method: "DELETE",
    token,
  });
}

export type QuestSubmissionStatus = "submitted" | "approved" | "rejected";

export type QuizAnswerSelectionDto = number | number[];

export type AdminPendingSubmissionSummaryDto = {
  totalPending: number;
  latestLiveCampaignId?: string;
  campaigns: Array<{
    campaignId: string;
    campaignName: string;
    pendingCount: number;
  }>;
};

type QuestEvidenceUploadUrlResponse = {
  uploadUrl: string;
  objectPath: string;
  readUrl: string;
  expiresAt: string;
  contentType: string;
  maxBytes: number;
};

type QuestEvidenceFinalizeResponse = {
  objectPath: string;
  readUrl: string;
};

export type QuestSubmissionDto = {
  id: string;
  campaignId: string;
  communityId: string;
  questId: string;
  questTitle: string;
  userId: string;
  displayName?: string;
  verificationMode: "automatic" | "manual";
  status: QuestSubmissionStatus;
  payload: {
    text?: string;
    url?: string;
    fileUrl?: string;
    quizAnswers?: QuizAnswerSelectionDto[];
    quizResult?: {
      incorrectQuestionIndexes: number[];
    };
    specialRatioAction?: "like" | "retweet" | "reply";
    specialRatioIntent?: "assign" | "complete";
    specialRatioPhase?: "assigned" | "completed";
    smartScoringMode?: 'evaluate' | 'submit';
    smartScoringResult?: {
      score: number;
      passed: boolean;
      reasoning: string;
      improvements: string[];
      flags?: Array<
        "spam" | "low_effort" | "missing_required_terms" | "unsafe" | "off_topic"
      >;
    };
    questRef?: string;
    redirectUrl?: string;
    verifiedAccountEvent?: "account_verified";
  };
  reviewNote?: string;
  reviewedBy?: string;
  reviewedAt?: number;
  createdAt: number;
  updatedAt: number;
  attemptNumber: number;
  previousSubmissionId?: string | null;
};

export type SmartScoringDraftEvaluationDto = NonNullable<
  QuestSubmissionDto["payload"]["smartScoringResult"]
>;

export type VerifiedAccountWebhookSecretStatusDto = {
  configured: boolean;
  communityId: string;
  createdAt?: number;
  updatedAt?: number;
};

export type VerifiedAccountWebhookSecretIssueDto =
  VerifiedAccountWebhookSecretStatusDto & {
    secret: string;
  };

export type PublicCampaignMemberActivityDto = {
  questId: string;
  timestamp: number;
  status: "approved" | "rejected";
};

export async function participateQuestApi(params: {
  token: string;
  campaignId: string;
  questId: string;
  payload?: {
    text?: string;
    url?: string;
    fileUrl?: string;
    quizAnswers?: QuizAnswerSelectionDto[];
    smartScoringMode?: 'evaluate' | 'submit';
    specialRatioIntent?: "assign" | "complete";
  };
}) {
  return _apiRequest<QuestSubmissionDto>(
    `/v1/campaigns/${params.campaignId}/quests/${params.questId}/participate`,
    {
      method: "POST",
      token: params.token,
      data: params.payload ?? {},
    },
  );
}

export async function evaluateSmartScoringDraftApi(params: {
  token: string;
  campaignId: string;
  questId: string;
  text: string;
}) {
  return _apiRequest<SmartScoringDraftEvaluationDto>(
    `/v1/campaigns/${params.campaignId}/quests/${params.questId}/smart-scoring/evaluate`,
    {
      method: "POST",
      token: params.token,
      data: {
        text: params.text,
      },
    },
  );
}

export async function fetchVerifiedAccountWebhookSecretStatusApi(params: {
  token?: string;
  campaignId: string;
}) {
  return _apiRequest<VerifiedAccountWebhookSecretStatusDto>(
    `/v1/campaigns/${params.campaignId}/verified-account/secret`,
    {
      token: params.token,
    },
  );
}

export async function issueVerifiedAccountWebhookSecretApi(params: {
  token?: string;
  campaignId: string;
}) {
  return _apiRequest<VerifiedAccountWebhookSecretIssueDto>(
    `/v1/campaigns/${params.campaignId}/verified-account/secret`,
    {
      method: "POST",
      token: params.token,
    },
  );
}

export async function requestQuestEvidenceUploadUrlApi(params: {
  token: string;
  campaignId: string;
  questId: string;
  contentType: string;
}) {
  return _apiRequest<QuestEvidenceUploadUrlResponse>(
    `/v1/campaigns/${params.campaignId}/quests/${params.questId}/evidence/upload-url`,
    {
      method: "POST",
      token: params.token,
      data: {
        contentType: params.contentType,
      },
    },
  );
}

export async function finalizeQuestEvidenceUploadApi(params: {
  token: string;
  campaignId: string;
  questId: string;
  objectPath: string;
}) {
  return _apiRequest<QuestEvidenceFinalizeResponse>(
    `/v1/campaigns/${params.campaignId}/quests/${params.questId}/evidence/finalize`,
    {
      method: "POST",
      token: params.token,
      data: {
        objectPath: params.objectPath,
      },
    },
  );
}

export async function fetchMyCampaignSubmissionsApi(params: {
  token: string;
  campaignId: string;
  questId?: string;
  limit?: number;
}) {
  return _apiRequest<{ items: QuestSubmissionDto[] }>(
    `/v1/campaigns/${params.campaignId}/submissions/mine`,
    {
      token: params.token,
      params: {
        questId: params.questId,
        limit: params.limit,
      },
    },
  );
}

export async function fetchPublicCampaignMemberActivitiesApi(params: {
  campaignId: string;
  userId: string;
  limit?: number;
  cursor?: string;
}) {
  return _apiRequest<{
    items: PublicCampaignMemberActivityDto[];
    nextCursor?: string;
  }>(
    `/v1/campaigns/${params.campaignId}/members/${params.userId}/activities`,
    {
      authRequired: false,
      params: {
        limit: params.limit,
        cursor: params.cursor,
      },
    },
  );
}

export async function reviewCampaignSubmissionApi(params: {
  token: string;
  campaignId: string;
  submissionId: string;
  decision: "approved" | "rejected";
  note?: string;
}) {
  return _apiRequest<QuestSubmissionDto>(
    `/v1/campaigns/${params.campaignId}/submissions/${params.submissionId}/review`,
    {
      method: "POST",
      token: params.token,
      data: {
        decision: params.decision,
        note: params.note,
      },
    },
  );
}

export async function fetchAdminSubmissionsApi(params: {
  token: string;
  communityId: string;
  campaignId?: string;
  questId?: string;
  userId?: string;
  status?: QuestSubmissionStatus;
  verificationMode?: QuestSubmissionDto["verificationMode"];
  cursor?: string;
  limit?: number;
}) {
  return _apiRequest<{ items: QuestSubmissionDto[]; nextCursor?: string }>(
    "/v1/admin/submissions",
    {
      token: params.token,
      params: {
        communityId: params.communityId,
        campaignId: params.campaignId,
        questId: params.questId,
        userId: params.userId,
        status: params.status,
        verificationMode: params.verificationMode,
        cursor: params.cursor,
        limit: params.limit,
      },
    },
  );
}

export async function fetchAdminPendingSubmissionSummaryApi(params: {
  token: string;
  communityId: string;
}) {
  return _apiRequest<AdminPendingSubmissionSummaryDto>(
    "/v1/admin/submissions/pending-summary",
    {
      token: params.token,
      params: {
        communityId: params.communityId,
      },
    },
  );
}
